import { useState, useEffect } from "react";
import ChatWidget from "../components/ChatWidget";
import { identifyVisitor } from "../utils/visitor-identification";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BASE_URL = "https://react-chatwidget-flowpat.vercel.app";

// Org/widget shown on first open (Phytoceutics). Changeable via the dropdown.
const DEFAULT_ORGANIZATION_ID = "08becb51-9734-432e-a1e8-3a2115c0f3eb";
const DEFAULT_WIDGET_ID = "f9d41d75-2f19-428c-99ec-cda027c7bcda";

type WidgetOrg = {
  organizationId: string;
  organizationName: string;
  widgetId: string;
  headerText: string;
  widgetCount: number;
};

export default function Index() {
  // Committed identifiers that drive the widget fetch.
  const [organizationId, setOrganizationId] = useState(DEFAULT_ORGANIZATION_ID);
  const [widgetId, setWidgetId] = useState(DEFAULT_WIDGET_ID);

  // List of organisations that have widgets (for the dropdown).
  const [orgs, setOrgs] = useState<WidgetOrg[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgsError, setOrgsError] = useState("");

  const [showWidget, setShowWidget] = useState(false);
  const [widgetConfig, setWidgetConfig] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isRyloMode, setIsRyloMode] = useState(false);
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);

  // Load the list of organisations that have widgets.
  useEffect(() => {
    const loadOrgs = async () => {
      setOrgsLoading(true);
      setOrgsError("");
      try {
        const res = await fetch(`${BASE_URL}/api/list-widget-orgs`);
        if (!res.ok) throw new Error("Failed to load organisations");
        const json = await res.json();
        setOrgs(Array.isArray(json.orgs) ? json.orgs : []);
      } catch (err) {
        setOrgsError(
          err instanceof Error ? err.message : "Failed to load organisations"
        );
      } finally {
        setOrgsLoading(false);
      }
    };
    loadOrgs();
  }, []);

  const fetchWidgetConfig = async (orgId: string, wId: string) => {
    setLoading(true);
    setError("");
    setShowWidget(false);

    try {
      const response = await fetch(
        `${BASE_URL}/api/config-chat-widget?organizationId=${encodeURIComponent(
          orgId
        )}&widgetId=${encodeURIComponent(wId)}`
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Failed to fetch widget configuration"
        );
      }

      const data = await response.json();
      setWidgetConfig(data);
      setShowWidget(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
      setShowWidget(false);
    } finally {
      setLoading(false);
    }
  };

  // Load the selected org/widget on mount and whenever the selection changes.
  useEffect(() => {
    if (organizationId && widgetId) {
      fetchWidgetConfig(organizationId, widgetId);
    }
  }, [organizationId, widgetId]);

  const handleSelectOrg = (orgId: string) => {
    const entry = orgs.find((o) => o.organizationId === orgId);
    if (!entry) return;
    setOrganizationId(entry.organizationId);
    setWidgetId(entry.widgetId);
  };

  const selectedOrg = orgs.find((o) => o.organizationId === organizationId);

  return (
    <div className="container mx-auto p-8 max-w-4xl">
      <h1 className="text-3xl font-bold mb-8">
        Chat Widget Configuration Tester
      </h1>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Widget Configuration</CardTitle>
          <CardDescription>
            Showing org ID: <strong>{organizationId}</strong> · widget ID:{" "}
            <strong>{widgetId}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Organisation picker (orgs that have widgets) */}
          <div className="space-y-1.5 max-w-md">
            <Label htmlFor="org-select">Organisation</Label>
            <Select
              value={organizationId}
              onValueChange={handleSelectOrg}
              disabled={orgsLoading || orgs.length === 0}
            >
              <SelectTrigger id="org-select">
                <SelectValue
                  placeholder={
                    orgsLoading
                      ? "Loading organisations…"
                      : "Select an organisation"
                  }
                >
                  {selectedOrg
                    ? `${selectedOrg.organizationName}${
                        selectedOrg.widgetCount > 1
                          ? ` (${selectedOrg.widgetCount} widgets)`
                          : ""
                      }`
                    : undefined}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {orgs.map((o) => (
                  <SelectItem key={o.organizationId} value={o.organizationId}>
                    {o.organizationName}
                    {o.widgetCount > 1 ? ` (${o.widgetCount} widgets)` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {orgsError && (
              <p className="text-xs text-red-600">
                {orgsError}. The widget list endpoint may not be deployed yet —
                the default ({organizationId}) still loads below.
              </p>
            )}
            {!orgsLoading && !orgsError && orgs.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No organisations found. (Deploy <code>api/list-widget-orgs</code>{" "}
                to populate this list.)
              </p>
            )}
          </div>

          {loading && <p className="mt-4">Loading widget configuration...</p>}

          <div className="flex items-center justify-between mt-6">
            <div className="flex items-center space-x-2">
              <Switch
                id="rylo-mode"
                checked={isRyloMode}
                onCheckedChange={setIsRyloMode}
              />
              <Label htmlFor="rylo-mode">
                Enable Rylo Mode (Central Modal Design)
              </Label>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center space-x-2">
                <Switch
                  id="2fa-mode"
                  checked={twoFaEnabled}
                  onCheckedChange={setTwoFaEnabled}
                />
                <Label htmlFor="2fa-mode">2FA</Label>
              </div>
              <button
                onClick={() => {
                  localStorage.removeItem(`flowapt_user_${organizationId}`);
                  localStorage.removeItem(`flowapt_contact_${organizationId}`);
                  localStorage.removeItem(`flowapt_visitor_${organizationId}`);
                  window.location.reload();
                }}
                className="text-xs px-3 py-1.5 rounded border border-red-300 text-red-600 hover:bg-red-50 transition-colors"
              >
                Reset Session
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-8">
          <p>{error}</p>
        </div>
      )}

      {showWidget && widgetConfig && (
        <div style={{ height: "600px", position: "relative" }}>
          <ChatWidget
            key={`${organizationId}-${widgetId}`}
            organizationId={organizationId}
            widgetId={widgetId}
            visitorInfo={identifyVisitor(organizationId)}
            position={widgetConfig.position || "bottom-right"}
            theme="light"
            headerText={isRyloMode ? "Rylo" : widgetConfig.headerText}
            subheaderText={widgetConfig.subheaderText}
            preloginHeaderText={widgetConfig.preloginHeaderText}
            preloginSubheaderText={widgetConfig.preloginSubheaderText}
            agentName={widgetConfig.agentName}
            homeDesign={widgetConfig.homeDesign}
            customIcons={{
              userWeb: widgetConfig.customIcons?.userWeb ? (
                <img
                  src={widgetConfig.customIcons.userWeb.url}
                  alt="Home Icon"
                  className="w-full h-full object-contain"
                />
              ) : undefined,
              botWeb: widgetConfig.customIcons?.botWeb ? (
                <img
                  src={widgetConfig.customIcons.botWeb.url}
                  alt="Bot"
                  width={16}
                  height={16}
                />
              ) : undefined,
              humanWeb: widgetConfig.customIcons?.humanWeb ? (
                <img
                  src={widgetConfig.customIcons.humanWeb.url}
                  alt="Human"
                  width={16}
                  height={16}
                />
              ) : undefined,
            }}
            customColors={widgetConfig.customColors}
            iconDisplay={widgetConfig.iconDisplay}
            systemPrompt={widgetConfig.systemPrompt}
            ryloMode={isRyloMode}
            previewMode={isRyloMode ? "yes" : undefined}
            twoFa={twoFaEnabled}
            whatsapp={widgetConfig.whatsapp}
            shopifyWebsite={widgetConfig.shopifyWebsite}
            centerConfig={widgetConfig.centerConfig}
            launcher={widgetConfig.launcher}
            hiddenPaths={widgetConfig.hiddenPaths}
          />
        </div>
      )}
    </div>
  );
}
